import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Instagram, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { axiosInstance } from "@/App";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const AccountDeletion = ({ user }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(user));
  const [deleting, setDeleting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [phrase, setPhrase] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [details, setDetails] = useState("");
  const [availableProviders, setAvailableProviders] = useState({});

  const loadStatus = () => {
    if (!user) return;
    setLoading(true);
    axiosInstance.get("/account/deletion/status")
      .then(({ data }) => setStatus(data))
      .catch((error) => toast.error(error.response?.data?.detail || "Could not load deletion status"))
      .finally(() => setLoading(false));
  };

  useEffect(loadStatus, [user?.id]);

  useEffect(() => {
    if (!user) return;
    axiosInstance.get("/auth/social/providers")
      .then(({ data }) => setAvailableProviders(data || {}))
      .catch(() => setAvailableProviders({}));
  }, [user?.id]);

  const providers = useMemo(
    () => status?.providers?.length ? status.providers : ["google"],
    [status?.providers],
  );

  const startReauthentication = (provider) => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const query = new URLSearchParams({
      purpose: "account_delete",
      return_to: "/account-deletion",
    });
    window.location.href = `${backend}/api/auth/social/${provider}/login?${query}`;
  };

  const startLinking = (provider) => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const query = new URLSearchParams({
      purpose: "link",
      return_to: "/settings/account",
    });
    window.location.href = `${backend}/api/auth/social/${provider}/login?${query}`;
  };

  const submitPublicRequest = async (event) => {
    event.preventDefault();
    setRequesting(true);
    try {
      const { data } = await axiosInstance.post("/account/deletion-request", { email, details });
      toast.success(data.message);
      setDetails("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Deletion request could not be submitted");
    } finally {
      setRequesting(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete("/account", {
        data: {
          confirmation_email: email,
          confirmation_phrase: phrase,
        },
      });
      try { localStorage.removeItem("session_token"); } catch (error) {}
      window.location.replace("/account-deletion?deleted=1");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 401) {
        toast.error("Verify your identity again before deleting your account.");
        loadStatus();
      } else if (typeof detail === "object") {
        toast.error(detail.message || "Support assistance is required");
        setStatus((current) => ({ ...current, blockers: detail.blockers || current?.blockers }));
      } else {
        toast.error(detail || "Your account could not be deleted");
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#080b12] px-4 py-8 text-white">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex items-center justify-between">
          <button type="button" onClick={() => navigate(user ? "/creator/settings" : "/")} className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <BrandLogo imageClassName="h-9 w-auto" />
        </div>

        <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl sm:p-8" data-testid="account-deletion-page">
          <div className="mb-6 flex items-start gap-4">
            <div className="rounded-2xl bg-red-500/15 p-3 text-red-300"><Trash2 className="h-7 w-7" /></div>
            <div>
              <h1 className="text-2xl font-black sm:text-3xl">Delete your ShowMeLive account</h1>
              <p className="mt-2 text-sm leading-6 text-gray-400">
                This permanently removes your account, ends every session, and removes or anonymizes associated personal data.
              </p>
            </div>
          </div>

          {!user ? (
            <form onSubmit={submitPublicRequest} className="space-y-4">
              <div className="rounded-2xl border border-blue-400/20 bg-blue-500/10 p-4 text-sm leading-6 text-blue-100">
                Sign in to delete automatically. If you cannot access your account, submit a deletion request here and support will verify ownership.
              </div>
              <label className="block">
                <span className="mb-2 block text-sm font-bold">Account email</span>
                <Input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} className="border-white/15 bg-black/30" />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold">Optional details</span>
                <Textarea value={details} onChange={(event) => setDetails(event.target.value)} className="min-h-28 border-white/15 bg-black/30" placeholder="Tell support why you cannot complete deletion in the app." />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <Button type="button" onClick={() => navigate("/")} className="h-12 bg-blue-600 hover:bg-blue-700">Sign in to continue</Button>
                <Button type="submit" disabled={requesting} className="h-12 bg-gray-700 hover:bg-gray-600">
                  {requesting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Request deletion
                </Button>
              </div>
            </form>
          ) : loading ? (
            <div className="flex min-h-48 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-400" /></div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-2xl border border-white/10 bg-black/30 p-5">
                <h2 className="flex items-center gap-2 font-extrabold">
                  <Instagram className="h-5 w-5 text-pink-400" />Connected sign-in methods
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-400">
                  Connect Instagram to this existing ShowMeLive account before using Continue with Instagram.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {providers.includes("instagram") ? (
                    <div className="inline-flex h-11 items-center gap-2 rounded-lg border border-emerald-400/25 bg-emerald-500/10 px-4 text-sm font-bold text-emerald-200">
                      <CheckCircle2 className="h-4 w-4" />Instagram connected
                    </div>
                  ) : availableProviders.instagram ? (
                    <Button type="button" onClick={() => startLinking("instagram")} className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 font-bold text-white hover:opacity-90">
                      <Instagram className="mr-2 h-4 w-4" />Connect Instagram
                    </Button>
                  ) : (
                    <div className="text-sm text-gray-500">Instagram connection is not configured.</div>
                  )}
                </div>
              </div>

              <div>
                <h2 className="mb-3 text-lg font-extrabold">What happens</h2>
                <ul className="space-y-2 text-sm leading-6 text-gray-300">
                  {(status?.retention || []).map((item) => (
                    <li key={item} className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-blue-400" />{item}</li>
                  ))}
                </ul>
              </div>

              {status?.blockers?.length > 0 && (
                <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4">
                  <h2 className="flex items-center gap-2 font-extrabold text-amber-200"><AlertTriangle className="h-5 w-5" />Deletion needs assistance</h2>
                  <ul className="mt-2 space-y-2 text-sm text-amber-100">
                    {status.blockers.map((blocker) => <li key={blocker.code}>{blocker.message}</li>)}
                  </ul>
                  <a className="mt-3 inline-block text-sm font-bold text-blue-300 underline" href={`mailto:${status.support_email}`}>Contact {status.support_email}</a>
                </div>
              )}

              {!status?.reauthenticated ? (
                <div className="rounded-2xl border border-white/10 bg-black/30 p-5">
                  <h2 className="flex items-center gap-2 font-extrabold"><ShieldCheck className="h-5 w-5 text-blue-400" />Verify your identity</h2>
                  <p className="mt-2 text-sm text-gray-400">Reauthenticate before deletion. Verification remains valid for {status?.reauthentication_window_minutes || 15} minutes.</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {providers.map((provider) => (
                      <Button key={provider} type="button" onClick={() => startReauthentication(provider)} className="bg-white text-black hover:bg-gray-200">
                        Continue with {provider === "twitter" ? "X" : provider.charAt(0).toUpperCase() + provider.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4 rounded-2xl border border-red-400/25 bg-red-500/[0.07] p-5">
                  <div className="flex items-center gap-2 font-extrabold text-red-200"><ShieldCheck className="h-5 w-5" />Identity verified</div>
                  <label className="block">
                    <span className="mb-2 block text-sm font-bold">Confirm your email</span>
                    <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} className="border-red-300/20 bg-black/40" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-bold">Type DELETE</span>
                    <Input value={phrase} onChange={(event) => setPhrase(event.target.value)} className="border-red-300/20 bg-black/40" />
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 text-sm leading-5 text-gray-300">
                    <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} className="mt-1" />
                    I understand that account deletion is permanent and cannot be undone.
                  </label>
                  <Button
                    type="button"
                    onClick={deleteAccount}
                    disabled={deleting || !acknowledged || phrase !== "DELETE" || status?.blockers?.length > 0}
                    className="h-12 w-full bg-red-600 font-black hover:bg-red-700"
                    data-testid="confirm-account-deletion"
                  >
                    {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    Permanently delete my account
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default AccountDeletion;
